import { ref } from 'vue'

/**
 * Print context — small reactive layer that lets PagedTable's
 * print flow tell PrintLayout *what* is being printed before the
 * browser snapshots the page.
 *
 * `route.name` is fine when the user prints a whole dashboard,
 * but when they print one section (a tab, a modal) the route is
 * still "Summary" even though the printed content is specifically
 * the active tab or modal table. This module lets the click on
 * PagedTable's Print button derive a more specific name from the
 * DOM context and surface it on the printout.
 */
export const printContextReportName = ref<string | null>(null)

/**
 * Read the print context for a given table element. Walks the
 * DOM upward looking for:
 *   1. A UiModal container — uses its `data-print-title` override
 *      attribute if set, otherwise the modal's visible title
 *   2. A tab panel — uses the active tab's title
 *
 * Returns '' when none apply, in which case PrintLayout falls
 * back to `route.name`.
 */
export function resolveReportNameFromDom (tableEl: Element | null): string {
  if (!tableEl) return ''

  // 1. Inside a UiModal — prefer the modal's printTitle prop
  //    (rendered as `data-print-title` on the container), else
  //    the modal's visible title. This lets callers print under
  //    a name that differs from the modal's user-facing chrome
  //    (e.g. "Top Performers - Business Units (Cost Centres)"
  //    even when the modal itself reads "All Business Units").
  const modal = tableEl.closest('.ui-modal__container')
  if (modal) {
    const override = modal.getAttribute('data-print-title')
    if (override) return override.trim()
    const title = modal
      .querySelector<HTMLElement>('.ui-modal__title')
      ?.textContent?.trim()
    if (title) return title
  }

  // 2. Inside a tab panel — walk up to find the matching tab
  //    strip and pick the aria-selected button. The tab strip is
  //    a sibling of the panel under the TabGroup root, so we
  //    walk parents until we find one that contains a tablist.
  const tabPanel = tableEl.closest('[role="tabpanel"]')
  if (tabPanel) {
    let cursor: Element | null = tabPanel.parentElement
    while (cursor && cursor !== document.body) {
      const tablist = cursor.querySelector('[role="tablist"]')
      if (tablist) {
        const active = tablist.querySelector<HTMLElement>(
          '[role="tab"][aria-selected="true"]',
        )
        const label = active?.textContent?.trim()
        if (label) return label
      }
      cursor = cursor.parentElement
    }
  }

  return ''
}
